import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Center, Group, Loader, Paper, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiError, type LexiconPrice, lexiconApi } from '../../api';
import { formatAmount, formatDayOf } from '../../format';

const PAGE = 200;

/**
 * Prices from the Baupreislexikon (ADR 0023), as the night round fetched
 * them: with price date and fetch date at every value. Same access as the
 * benchmark catalog; read only - a value fetched again replaces the old one.
 */
export function LexiconTab(): JSX.Element {
  const [query, setQuery] = useState('');
  const [q] = useDebouncedValue(query.trim(), 300);
  const phone = useMediaQuery('(max-width: 48em)');
  const list = useInfiniteQuery({
    queryKey: ['lexicon', q],
    queryFn: ({ pageParam }) => lexiconApi.list(q, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (pages.length * PAGE < last.total ? pages.length * PAGE : undefined),
  });
  const rows = list.data?.pages.flatMap((p) => p.rows) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const range = (r: LexiconPrice): string => [r.low, r.mid, r.high].map((v) => (v === null ? '—' : formatAmount(v))).join(' · ');
  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Preise aus dem Baupreislexikon, abgerufen mit dem Konto des Büros im Nachtumlauf (Einstellungen › Abzüge). Jeder Wert trägt den Preisstand des Portals und das Abrufdatum; eine Kostengruppe nur, wenn das Portal sie nennt.
      </Text>
      <Group gap="sm">
        <TextInput size="xs" placeholder="Suchen: Bezeichnung oder Schlüssel …" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1 }} />
        <Text size="xs" c="dimmed">{total.toLocaleString('de-DE')} {total === 1 ? 'Wert' : 'Werte'}</Text>
      </Group>
      {list.isPending && <Center h={60}><Loader size="sm" /></Center>}
      {list.isError && <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Die Werte konnten nicht geladen werden.'}</Alert>}
      {list.data && rows.length === 0 && <Text size="sm" c="dimmed">{q ? 'Keine Treffer.' : 'Noch keine Werte. Sie kommen mit dem Nachtumlauf, sobald der Abzugsdienst Häppchen für das Baupreislexikon ausführt.'}</Text>}
      {rows.length > 0 && (phone ? (
        <Stack gap="xs">
          {rows.map((r) => (
            <Paper key={r.id} withBorder p="xs" radius="md">
              <Text size="sm">{r.label}</Text>
              <Text size="xs">{range(r)}{r.unit ? ` €/${r.unit}` : ''}</Text>
              <Text size="xs" c="dimmed">{[r.costGroup && `KG ${r.costGroup}`, r.region, r.priceDate && `Preisstand ${r.priceDate}`, `abgerufen ${formatDayOf(r.fetchedAt)}`].filter(Boolean).join(' · ')}</Text>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Table.ScrollContainer minWidth={900} maxHeight={520}>
          <Table verticalSpacing={2} className="pst-tabular" highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>KG</Table.Th>
                <Table.Th>Bezeichnung</Table.Th>
                <Table.Th w={70}>Einheit</Table.Th>
                <Table.Th w={90} ta="right">von</Table.Th>
                <Table.Th w={90} ta="right">Mittel</Table.Th>
                <Table.Th w={90} ta="right">bis</Table.Th>
                <Table.Th w={130}>Region</Table.Th>
                <Table.Th w={110}>Preisstand</Table.Th>
                <Table.Th w={95}>abgerufen</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td><Text size="xs" ff="monospace">{r.costGroup ?? ''}</Text></Table.Td>
                  <Table.Td><Text size="sm">{r.label}</Text><Text size="xs" c="dimmed" ff="monospace">{r.key}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.unit ?? ''}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{r.low === null ? '—' : formatAmount(r.low)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={600}>{r.mid === null ? '—' : formatAmount(r.mid)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{r.high === null ? '—' : formatAmount(r.high)}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.region ?? ''}</Text></Table.Td>
                  <Table.Td><Text size="xs">{r.priceDate ?? ''}</Text></Table.Td>
                  <Table.Td><Text size="xs">{formatDayOf(r.fetchedAt)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ))}
      {list.hasNextPage && (
        <Group><Button size="xs" variant="subtle" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>weitere {Math.min(PAGE, total - rows.length).toLocaleString('de-DE')} laden</Button></Group>
      )}
    </Stack>
  );
}
